import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import { FolderIcon, DocumentIcon, ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';

const ProjectFiles = ({ projectId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  useEffect(() => {
    fetchFiles();
  }, [projectId]);
  
  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Fetching project files for project ID: ${projectId}`);
      const response = await api.get(`/files/project/${projectId}`);
      
      console.log('Project files response:', response.data);
      
      if (response.data && response.data.files) {
        setFiles(response.data.files);
      } else {
        setFiles([]);
      }
    } catch (err) {
      console.error('Error fetching project files:', err);
      console.error('Response data:', err.response?.data);
      console.error('Status:', err.response?.status);
      setError(`Failed to load project files. ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check file size limit (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size exceeds the 10MB limit.');
        return;
      }
      
      setSelectedFile(file);
    }
  };
  
  const handleUpload = async () => {
    if (!selectedFile) return;
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('is_internal', false); // Make client-visible by default
      
      console.log(`Uploading file to project: ${projectId}`);
      console.log('File details:', {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type
      });
      
      // Add auth check logging
      const token = localStorage.getItem('token');
      console.log('Auth token present:', !!token);
      if (token) {
        try {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          console.log('Token user:', {
            id: decoded.id,
            name: decoded.name,
            email: decoded.email
          });
        } catch (e) {
          console.error('Unable to decode token:', e);
        }
      }
      
      const response = await api.post(
        `/files/upload/project/${projectId}`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      console.log('Upload response:', response.data);
      
      if (response.data && response.data.file) {
        // Add new file to the list
        setFiles([response.data.file, ...files]);
        
        // Reset the file input
        setSelectedFile(null);
        
        toast.success('File uploaded successfully');
      }
    } catch (error) {
      console.error('File upload error:', error);
      console.error('Response data:', error.response?.data);
      console.error('Status:', error.response?.status);
      toast.error(error.response?.data?.error || 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await api.delete(`/files/${fileId}`);
      
      // Remove the file from the state
      setFiles(files.filter(file => file.id !== fileId));
      
      toast.success('File deleted successfully');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file. Please try again.');
    }
  };
  
  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await api.get(`/files/download/${fileId}`);
      
      if (response.data && response.data.success && response.data.downloadUrl) {
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.setAttribute('download', response.data.filename || fileName);
        link.setAttribute('target', '_blank');
        
        // Trigger a click on this link to start the download
        document.body.appendChild(link);
        link.click();
        
        // Clean up by removing the link
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      } else {
        throw new Error('Invalid download response');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file. Please try again.');
    }
  };
  
  // Format file size to human readable
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Determine file icon
  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return <DocumentIcon className="h-5 w-5 text-blue-500" />;
    } else if (mimeType.includes('pdf')) {
      return <DocumentIcon className="h-5 w-5 text-red-500" />;
    } else if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
      return <DocumentIcon className="h-5 w-5 text-green-500" />;
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return <DocumentIcon className="h-5 w-5 text-blue-700" />;
    } else {
      return <DocumentIcon className="h-5 w-5 text-gray-500" />;
    }
  };
  
  if (loading && files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48">
        <div className="w-10 h-10 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading files...</p>
      </div>
    );
  }
  
  if (error && files.length === 0) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={fetchFiles}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header with add button */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Project Files</h2>
        <div className="flex items-center space-x-2">
          <label className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
            <FolderIcon className="h-4 w-4 mr-1" />
            Select File
            <input type="file" className="hidden" onChange={handleFileChange} disabled={isUploading} />
          </label>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className={`inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              !selectedFile || isUploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
      
      {/* File name display */}
      {selectedFile && (
        <div className="mb-4">
          <div className="flex items-center bg-blue-50 p-2 rounded">
            <DocumentIcon className="h-5 w-5 text-blue-500 mr-2" />
            <span className="text-sm text-blue-800">{selectedFile.name}</span>
          </div>
          
          {/* Progress bar during upload */}
          {isUploading && (
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="bg-blue-600 h-2 rounded-full" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
        </div>
      )}
      
      {/* Files list */}
      {loading && (
        <div className="flex justify-center py-4">
          <div className="w-8 h-8 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        </div>
      )}
      
      {files.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visibility
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {files.map((file) => (
                <tr key={file.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getFileIcon(file.file_type || file.mime_type)}
                      <div className="ml-2">
                        <div className="text-sm font-medium text-gray-900">{file.original_filename}</div>
                        <div className="text-sm text-gray-500">
                          {file.uploaded_by_name || (file.uploaded_by_client ? 'Client' : 'Team')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatFileSize(file.file_size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(file.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      file.is_internal ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {file.is_internal ? 'Internal' : 'Client-Visible'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-3">
                      <button 
                        onClick={() => handleDownload(file.id, file.original_filename)} 
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        Download
                      </button>
                      <button
                        onClick={() => handleDelete(file.id)}
                        className="text-red-600 hover:text-red-900 flex items-center"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <p className="text-gray-500">No files have been uploaded to this project yet.</p>
        </div>
      )}
    </div>
  );
};

export default ProjectFiles; 